import React, { useContext } from "react";
import IOrder_Progress_State from "../../models/IOrder_Progress_State";
import ISubmitOrderDTO from "../../models/DTO/ISubmitOrderDTO";
import IOrder from "../../models/IOrder";
import UserContext from "../../contexts/UserContext";
import SupplierContext from "../../contexts/SupplierContext";
import UiContext from "../../contexts/UiContext";
import { TimelineNoteModal } from "../../components/order_entry/TimelineNoteModal/TimelineNoteModal";
import MinOrderTotalOverrideModal from "../../components/order_entry/MinOrderTotalOverrideModal/MinOrderTotalOverrideModal";
import { IMinimumOrderDisplayAmounts } from "../../services/MinimumOrderService";

/**
 * Shared hook for the order submit flow used by both the top submit button (OrderStepButtonsFn)
 * and the floating submit button (OrderEntryFloatingSubmit).
 *
 * Handles checking allowCustomMessage / addSubmitNote, showing the TimelineNoteModal,
 * confirming a supplier-user override of the minimum order total,
 * and building the full ISubmitOrderDTO payload.
 */
export const useSubmitOrderFlow = (submitFn: (payload: ISubmitOrderDTO) => void, order: IOrder) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const buildAndSubmit = (
        nextStep: IOrder_Progress_State,
        note?: string,
        internalNote?: string,
        customMessage?: string,
        overrideMinOrderTotal?: boolean
    ) => {
        const payload: ISubmitOrderDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: order.guid,
            order_state_id: nextStep.order_state_id,
        };

        if (note) {
            payload.stateChangeNote = note;
        }

        if (internalNote) {
            payload.stateChangeInternalNote = internalNote;
        }

        if (customMessage) {
            payload.customMessage = customMessage;
        }

        if (overrideMinOrderTotal) {
            payload.override_min_order_total = true;
        }

        submitFn(payload);
    };

    const showNoteModal = (step?: IOrder_Progress_State, overrideMinOrderTotal?: boolean) => {
        const commitModal = (nextStep?: IOrder_Progress_State, note?: string, internalNote?: string, customMessage?: string) => {
            if (nextStep) {
                buildAndSubmit(nextStep, note, internalNote, customMessage, overrideMinOrderTotal);
                closeModal();
            }
        };

        const modalComponent = React.createElement(TimelineNoteModal, {
            closeModalFn: closeModal,
            commitModalFn: commitModal,
            nextStep: step,
            userModel: userModel,
        });

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const proceed = (nextStep: IOrder_Progress_State, addSubmitNote?: boolean, overrideMinOrderTotal?: boolean) => {
        if (addSubmitNote || nextStep.allowCustomMessage) {
            showNoteModal(nextStep, overrideMinOrderTotal);
        } else {
            buildAndSubmit(nextStep, undefined, undefined, undefined, overrideMinOrderTotal);
        }
    };

    /**
     * Single entry point for submitting an order to the next workflow step.
     * When `minOrderOverride` is given (a supplier user submitting below the minimum order total), a confirm
     * modal comes first and the submit carries `override_min_order_total`.
     * Then shows the TimelineNoteModal if the next step has allowCustomMessage or addSubmitNote is true.
     * Otherwise submits directly.
     */
    const performSubmit = (
        nextStep?: IOrder_Progress_State,
        addSubmitNote?: boolean,
        minOrderOverride?: IMinimumOrderDisplayAmounts | null
    ) => {
        if (!nextStep) {
            return;
        }

        if (minOrderOverride) {
            const modalComponent = React.createElement(MinOrderTotalOverrideModal, {
                amounts: minOrderOverride,
                cancelFn: closeModal,
                confirmFn: () => {
                    closeModal();
                    proceed(nextStep, addSubmitNote, true);
                },
            });

            uiContext.setModalComponent(modalComponent);
            uiContext.showModal(true);
        } else {
            proceed(nextStep, addSubmitNote);
        }
    };

    return { performSubmit };
};
